import { parseFilesFromPayload } from '../../components/fields/field.projector';
import { FileRef } from '../../components/fields/file-upload/file-upload.types';
import { ChecklistItemStatus } from '../types/ChecklistItemStatus.enum';
import { Checklist, ChecklistItem } from '../types/ChecklistTypes';

export function findItem(
  checklist: Checklist,
  sectionId: string,
  itemId: string
): ChecklistItem | null {
  const section = checklist.sections.find(s => s.id === sectionId);
  return section?.items.find(i => i.id === itemId) ?? null;
}

export function createUpdatedChecklist(
  checklist: Checklist,
  sectionId: string,
  itemId: string,
  updates: Partial<ChecklistItem>
): Checklist {
  return {
    ...checklist,
    sections: checklist.sections.map(section =>
      section.id === sectionId
        ? {
            ...section,
            items: section.items.map(item =>
              item.id === itemId ? { ...item, ...updates } : item
            ),
          }
        : section
    ),
  };
}

export function withoutItem(
  checklist: Checklist,
  sectionId: string,
  itemId: string
): Checklist {
  return {
    ...checklist,
    sections: checklist.sections.map(section =>
      section.id === sectionId
        ? {
            ...section,
            items: section.items.filter(item => item.id !== itemId),
          }
        : section
    ),
  };
}

export function patchItemInAnySection(
  checklist: Checklist,
  itemId: string,
  patchFor: (item: ChecklistItem) => Partial<ChecklistItem>
): Checklist {
  return {
    ...checklist,
    sections: checklist.sections.map(section =>
      section.items.some(item => item.id === itemId)
        ? {
            ...section,
            items: section.items.map(item =>
              item.id === itemId ? { ...item, ...patchFor(item) } : item
            ),
          }
        : section
    ),
  };
}

export function clearedItemPatch(
  item: ChecklistItem | null | undefined
): Partial<ChecklistItem> {
  return {
    status: ChecklistItemStatus.Unchecked,
    statusChanged: undefined,
    entryTime: undefined,
    undoTime: undefined,
    completionTime: undefined,
    statusChangedBy: undefined,
    remarks: undefined,
    customInputs: (item?.customInputs ?? []).map(customInput => ({
      ...customInput,
      payload: 'null',
    })),
    slotSelections: (item?.slotSelections ?? []).map(slotSelection => ({
      ...slotSelection,
      payload: 'null',
    })),
  };
}

export function customInputPayloadPatch(
  item: ChecklistItem,
  customInputId: string,
  payload: string,
  slotId?: string | null
): Partial<ChecklistItem> {
  const customInputs = (item.customInputs ?? []).map(customInput =>
    customInput.id === customInputId ? { ...customInput, payload } : customInput
  );
  return slotId
    ? {
        customInputs,
        ...slotSelectionPayloadPatch(item, slotId, payload),
      }
    : { customInputs };
}

export function slotSelectionPayloadPatch(
  item: ChecklistItem,
  slotId: string,
  payload: string
): Partial<ChecklistItem> {
  return {
    slotSelections: (item.slotSelections ?? []).map(slotSelection =>
      slotSelection.slotId === slotId
        ? { ...slotSelection, payload }
        : slotSelection
    ),
  };
}

export function mergeFieldData(data: string, value: string): string {
  try {
    const current = JSON.parse(data || '{}') as Record<string, unknown>;
    const changed = JSON.parse(value) as Record<string, unknown>;
    return JSON.stringify({ ...current, ...changed });
  } catch {
    return data;
  }
}

export function serializeCustomInputPayload(payload: unknown): string {
  if (payload === undefined) return 'null';
  try {
    return JSON.stringify(payload ?? null);
  } catch {
    return 'null';
  }
}

export function parseImageUploadPayload(payload: string | undefined): {
  files: FileRef[];
  [key: string]: unknown;
} {
  if (!payload) {
    return { files: [] };
  }

  try {
    const parsed = JSON.parse(payload) as unknown;
    if (typeof parsed !== 'object' || parsed === null) {
      return { files: [] };
    }
    return { ...parsed, files: parseFilesFromPayload(parsed) };
  } catch {
    return { files: [] };
  }
}

export function insertImageUploadFileRef(
  files: FileRef[],
  fileRef: FileRef,
  fileIndex: number | undefined,
  fileOrder: string[] | undefined
): FileRef[] {
  if (files.some(file => file.id === fileRef.id)) {
    return files;
  }

  const orderedIndex = getFileOrderRestoreIndex(files, fileRef.id, fileOrder);
  if (orderedIndex !== null) {
    return [
      ...files.slice(0, orderedIndex),
      fileRef,
      ...files.slice(orderedIndex),
    ];
  }

  const boundedIndex =
    typeof fileIndex === 'number' && Number.isFinite(fileIndex)
      ? Math.min(Math.max(Math.trunc(fileIndex), 0), files.length)
      : files.length;

  return [
    ...files.slice(0, boundedIndex),
    fileRef,
    ...files.slice(boundedIndex),
  ];
}

function getFileOrderRestoreIndex(
  files: FileRef[],
  fileId: string,
  fileOrder: string[] | undefined
): number | null {
  if (!fileOrder) {
    return null;
  }

  const originalIndex = fileOrder.indexOf(fileId);
  if (originalIndex < 0) {
    return null;
  }

  const currentIndexes = files
    .map((file, currentIndex) => ({
      currentIndex,
      originalIndex: fileOrder.indexOf(file.id),
    }))
    .filter(file => file.originalIndex >= 0);

  const successor = currentIndexes
    .filter(file => file.originalIndex > originalIndex)
    .sort((a, b) => a.originalIndex - b.originalIndex)[0];
  if (successor) {
    return successor.currentIndex;
  }

  const predecessor = currentIndexes
    .filter(file => file.originalIndex < originalIndex)
    .sort((a, b) => b.originalIndex - a.originalIndex)[0];
  if (predecessor) {
    return predecessor.currentIndex + 1;
  }

  return null;
}
